import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Wrench, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useState } from "react";
import type { MaintenanceTimelineEvent } from "@/lib/maintenance-analytics";
import { formatWorkOrderNumber } from "@/hooks/use-work-orders";

interface MaintenanceTimelineProps {
  events: MaintenanceTimelineEvent[];
}

const eventConfig = {
  maintenance: {
    icon: Wrench,
    color: 'bg-chart-2',
    borderColor: 'border-chart-2'
  },
  alert: {
    icon: AlertCircle,
    color: 'bg-destructive',
    borderColor: 'border-destructive'
  }
};

export function MaintenanceTimeline({ events }: MaintenanceTimelineProps) {
  const [filter, setFilter] = useState<MaintenanceTimelineEvent['type'] | 'all'>('all');
  const [showAll, setShowAll] = useState(false);

  const filteredEvents = events.filter(e => filter === 'all' || e.type === filter);
  const displayedEvents = showAll ? filteredEvents : filteredEvents.slice(0, 10);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "dd/MM/yyyy HH:mm", { locale: ptBR });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge variant="default" className="bg-success text-white">Concluída</Badge>;
      case 'in_progress':
        return <Badge className="bg-warning text-white">Em Andamento</Badge>;
      case 'open':
        return <Badge variant="outline">Aberta</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Linha do Tempo de Manutenções</CardTitle>
            <CardDescription>
              {filteredEvents.length} eventos registrados
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              Todos
            </Button>
            <Button
              variant={filter === 'maintenance' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('maintenance')}
            >
              <Wrench className="h-4 w-4 mr-1" />
              Manutenções
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {displayedEvents.length > 0 ? (
          <div className="space-y-4">
            {displayedEvents.map((event, index) => {
              const config = eventConfig[event.type];
              const Icon = config.icon;
              
              return (
                <div key={index} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`rounded-full p-2 ${config.color} text-white`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    {index < displayedEvents.length - 1 && (
                      <div className={`w-0.5 h-full min-h-8 ${config.borderColor} border-l-2`} />
                    )}
                  </div>
                  <div className="flex-1 pb-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h4 className="font-semibold">{event.title}</h4>
                          {event.orderNumber && (
                            <Badge variant="outline" className="font-mono text-xs">
                              {formatWorkOrderNumber(event.orderNumber)}
                            </Badge>
                          )}
                          {getStatusBadge(event.status)}
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-muted-foreground">
                            <strong>Veículo:</strong> {event.vehiclePlate}
                          </p>
                          {event.provider && (
                            <p className="text-sm text-muted-foreground">
                              <strong>Fornecedor:</strong> {event.provider}
                            </p>
                          )}
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(event.date)}
                          </p>
                        </div>
                      </div>
                      {event.cost !== undefined && (
                        <div className="text-right">
                          <p className="font-semibold">{formatCurrency(event.cost)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {filteredEvents.length > 10 && (
              <div className="text-center pt-4">
                <Button 
                  variant="outline" 
                  onClick={() => setShowAll(!showAll)}
                >
                  {showAll ? 'Mostrar menos' : `Carregar mais (${filteredEvents.length - 10} eventos)`}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">
            Nenhum evento encontrado
          </p>
        )}
      </CardContent>
    </Card>
  );
}
